import { NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { getActiveAppContext } from "app/tools/common"
import { getFormatedNumber } from "app/tools/number"
import { IMapForecastScreen } from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { getQuantiles } from "d3-tools/tools/common"
import { INVENTORY_MAP_VIEW_TYPES, MAP_INVENTORY_QUANTILE_STEPS } from "form/inventory/model"
import { getInventory, getMapViewType, setMapInventoryLegendData } from "form/inventory/tools/common"
import {
  CURSOR_DEFAULT,
  CURSOR_MOVE,
  DEFAULT_IMPRESSION_EXTRUSION_VALUE,
  IMPRESSIONS_SOURCE_ID,
  IMapInfo,
  INVENTORY_PITCH,
  MAPBOX_DEFAULT_LAYER_MAX_ZOOM,
  SCREENS_SOURCE_ID,
  SCREEN_IMAGE_10_ID
} from "form/map/model"
import { getDefaultMapInfo, state } from "form/map/state"
import { formActionCreator } from "form/store/actions"
import { getDistanceKind } from "form/tools/geo-targets"
import { Feature, Point } from "geojson"
import { ILocation } from "geometry/model"
import { getFeature } from "geometry/tools/common"
import { FitBoundsOptions, GeoJSONSourceRaw, Map, MapboxOptions, NavigationControl, ScaleControl } from "mapbox-gl"
import { MAP_COLOR, THEME_PALETTE_SPECIAL_TEXT_COLOR } from "ui/model"
import {
  getFeaturesExtent,
  getMapExtent,
  getMapInfo,
  getMapMouseCursor,
  getMapboxAccessToken,
  loadMapImages,
  setMapMouseCursor,
  setSourceData
} from "./common"
import { getGeoTargetMapImages } from "./geo-targets"
import {
  activateInventoryGeoTargetsView,
  createInventoryMapGeoTargetsLayers,
  deactivateInventoryGeoTargetsView
} from "./inventory-geo-targets"
import {
  activateInventoryImpressionsView,
  createInventoryMapImpressionsLayers,
  deactivateInventoryImpressionsView,
  getSquareFeature,
  setImpressionLayerStyleForData
} from "./inventory-impressions"
import { closeInventoryMapPopup, createInventoryMapPopupIfNeeded, isInventoryMapPopupOpened } from "./inventory-popup"
import {
  activateInventoryScreensView,
  createInventoryMapScreensLayers,
  deactivateInventoryScreensView,
  getScreensMapImages,
  setScreensLayerStyleForData
} from "./inventory-screens"
import { getMapboxLocale } from "./locale"
import { getMapResolution } from "./resolution"

export function setInventoryMapVisibility(isVisible: boolean, viewType?: INVENTORY_MAP_VIEW_TYPES): void {
  const rootState = getStore().getState()
  const inventory = getInventory()
  const visibilityChanged = inventory.isMapVisible !== isVisible
  if (!visibilityChanged && !viewType) {
    return
  }
  const context = rootState.app.activeContext
  if (!context) {
    throw Error("No app context is active")
  }
  if (!visibilityChanged && inventory.mapViewType === viewType) {
    return
  }
  if (viewType) {
    applyViewTypeOnInventoryMap(viewType)
  }
  getStore().dispatch(formActionCreator.setInventoryMapVisibility(context, isVisible, viewType))
}

export function createInventoryMap(containerId: string): Map {
  const container = document.getElementById(containerId)
  if (!container) {
    throw Error("Unable to get inventory container")
  }
  container.style.position = "relative"
  const options: MapboxOptions = {
    container,
    maxZoom: MAPBOX_DEFAULT_LAYER_MAX_ZOOM - 0.00001,
    renderWorldCopies: true,
    preserveDrawingBuffer: true,
    accessToken: getMapboxAccessToken(),
    style: "mapbox://styles/mapbox/light-v10"
  }
  const mapInfo: IMapInfo = state.inventory.mapInfo
  options.bearing = mapInfo.bearing
  options.locale = getMapboxLocale()
  options.pitch = mapInfo.pitch
  if (mapInfo.zoom !== 0) {
    options.zoom = mapInfo.zoom
  }
  if (mapInfo.boundaryBox) {
    options.bounds = [
      mapInfo.boundaryBox.sw.x,
      mapInfo.boundaryBox.sw.y,
      mapInfo.boundaryBox.ne.x,
      mapInfo.boundaryBox.ne.y
    ]
  }
  if (mapInfo.center) {
    options.center = [mapInfo.center.x, mapInfo.center.y]
  }
  const map = new Map(options)
  state.inventory.map = map
  map.addControl(new NavigationControl())
  state.inventory.scaleControl = new ScaleControl({ unit: getDistanceKind() })
  map.addControl(state.inventory.scaleControl)
  createInventoryMapPopupIfNeeded()
  setMapMouseCursor(map, CURSOR_DEFAULT)
  let mouseCursorBeforeMapMove = CURSOR_DEFAULT
  const loadMapData = () => {
    createInventoryMapSources(map)
    createInventoryMapGeoTargetsLayers(map)
    createInventoryMapScreensLayers(map)
    createInventoryMapImpressionsLayers(map)
    applyDefaultViewType()
    if (state.inventory.refreshDataOnMapOpening) {
      refreshInventoryMapData(map)
    }
  }
  const handleMapLoadError = (error: any) => {
    console.error("An error occured while loading the inventory map", error)
    map.off("error", handleMapLoadError)
    loadMapData()
  }
  map.on("error", handleMapLoadError)
  const onStyleLoaded = () => {
    if (map.isStyleLoaded()) {
      map.off("error", handleMapLoadError)
      loadMapData()
    } else {
      setTimeout(() => onStyleLoaded(), 200)
    }
  }
  loadMapImages(map, [...getGeoTargetMapImages(), ...getScreensMapImages()]).then(() => onStyleLoaded())
  map.on("movestart", () => {
    const currentMouseCursor: string = getMapMouseCursor(map)
    // it happens move-end event not to be triggered,
    // so we have to avoid the move cursor to be always displayed in this case
    if (currentMouseCursor !== CURSOR_MOVE) {
      mouseCursorBeforeMapMove = currentMouseCursor
      setMapMouseCursor(map, CURSOR_MOVE)
    }
  })
  map.on("moveend", () => {
    state.inventory.mapInfo = getMapInfo(map)
    setMapMouseCursor(map, mouseCursorBeforeMapMove)
    refreshInventoryMapData()
  })
  if (IS_NOT_PROD) {
    const win = window as any
    win.inventoryMap = map
  }
  return map
}

export function destroyInventoryMap(): void {
  if (state.inventory.map) {
    const context = getActiveAppContext()
    if (context) {
      getStore().dispatch(formActionCreator.setInventoryMapIsLoaded(context, false))
    }
    state.inventory.map.remove()
    state.inventory.map = null
    state.inventory.scaleControl = null
  }
}

export const changeInventoryIsImperial = (isImperial: boolean) => {
  if (!state.inventory.scaleControl) {
    return
  }
  state.inventory.scaleControl.setUnit(isImperial ? "imperial" : "metric")
}

export function resetInventoryMap(): void {
  if (isInventoryMapPopupOpened()) {
    closeInventoryMapPopup()
  }
  if (state.inventory.map) {
    destroyInventoryMap()
  }
  state.featuresByType = {
    coordinates: [],
    impressions: [],
    screens: [],
    shapes: [],
    intersectGeoTarget: null
  }
  state.inventory.isMapOpening = true
  state.inventory.isRefreshing = false
  state.inventory.refreshDataOnMapOpening = true
  state.inventory.screensStyleCircleColor = MAP_COLOR
  state.inventory.screensStyleCircleRadius = 15
  state.inventory.screensStyleTextColor = THEME_PALETTE_SPECIAL_TEXT_COLOR
  state.inventory.screensStyleIconImage = SCREEN_IMAGE_10_ID
  state.inventory.screensStyleExtrusion = DEFAULT_IMPRESSION_EXTRUSION_VALUE
  state.inventory.mapInfo = getDefaultMapInfo()
}

export function onInventoryMapContainerResize(width: number, height: number, isMaximized: boolean): void {
  if (state.inventory.map) {
    state.inventory.map.resize()
    if (isMaximized !== state.inventory.isMaximized) {
      setTimeout(() => (state as any).inventory.map.resize(), 100)
    }
  }
  state.inventory.isMaximized = isMaximized
  if (!isMaximized) {
    state.inventory.width = width
    state.inventory.height = height
  }
}

export function onInventoryMapContainerMove(position: ILocation): void {
  state.inventory.initialPositionInPixel = position
}

export function refreshInventoryMapData(map?: Map): void {
  if (!map) {
    map = state.inventory.map as Map
  }
  if (!map) {
    state.inventory.refreshDataOnMapOpening = true
    return
  }
  state.inventory.refreshDataOnMapOpening = false
  const inventory = getInventory()
  if (!inventory.isMapRefreshing) {
    getStore().dispatch(formActionCreator.setMapIsRefreshing(inventory.contextType, true))
  }
  state.inventory.currentRequest++
  applyRefreshMapInventory(state.inventory.currentRequest)
}

async function applyRefreshMapInventory(currentRequest: number): Promise<void> {
  state.inventory.isRefreshing = true
  const inventory = getInventory()
  try {
    const { map } = state.inventory
    if (map && inventory.tempCampaignId !== NOT_SAVED_ID) {
      const resolutionMetersPerPixel = getMapResolution(map)
      // fetch data server side
      const result = await campaignRPO.getCampaignMapForecastScreens(
        inventory.tempCampaignId,
        getMapExtent(map),
        // 80 is size of clusters in px
        resolutionMetersPerPixel * 120,
        true
      )
      if (currentRequest !== state.inventory.currentRequest) {
        return
      }
      const allScreenCounts: number[] = []
      const allScreenImpressions: number[] = []
      const allImpressions: number[] = []
      let mapMaxImpressionsValue: number = 0
      state.featuresByType.screens = []
      state.featuresByType.impressions = []
      if (result.screens) {
        for (const forecastScreen of result.screens) {
          if (forecastScreen.screen_count !== 1) {
            allScreenCounts.push(forecastScreen.screen_count)
            allImpressions.push(forecastScreen.impressions)
          } else {
            allScreenImpressions.push(forecastScreen.impressions)
          }
          if (forecastScreen.impressions > mapMaxImpressionsValue) {
            mapMaxImpressionsValue = forecastScreen.impressions
          }
          state.featuresByType.screens.push(
            getFeature(
              { x: forecastScreen.centroid_longitude, y: forecastScreen.centroid_latitude },
              {
                ...forecastScreen,
                formatted_screen_count: getFormatedNumber(forecastScreen.screen_count)
              }
            ) as Feature<Point, IMapForecastScreen>
          )
          state.featuresByType.impressions.push(
            getSquareFeature(
              { x: forecastScreen.centroid_longitude, y: forecastScreen.centroid_latitude },
              6 * resolutionMetersPerPixel,
              forecastScreen
            )
          )
        }
      }
      const mapImpressionsQuantiles = getQuantiles(allImpressions, MAP_INVENTORY_QUANTILE_STEPS)
      closeInventoryMapPopup()
      setScreensLayerStyleForData(
        getQuantiles(allScreenCounts, MAP_INVENTORY_QUANTILE_STEPS),
        mapImpressionsQuantiles,
        getQuantiles(allScreenImpressions, MAP_INVENTORY_QUANTILE_STEPS)
      )
      setImpressionLayerStyleForData(mapMaxImpressionsValue, resolutionMetersPerPixel)
      setMapInventoryLegendData({
        mapImpressionsQuantiles,
        mapMaxImpressionsValue
      })
      refreshInventoryMapSources()
      if (state.inventory.isMapOpening) {
        state.inventory.isMapOpening = false
        centerInventoryOnFeatures(map)
      }
    }
  } catch (error) {
    console.error(error)
  }
  getStore().dispatch(formActionCreator.setMapIsRefreshing(inventory.contextType, false))
  state.inventory.isRefreshing = false
}

const centerInventoryOnFeatures = (map: Map) => {
  let features: Feature[] = []
  const options: FitBoundsOptions = {
    padding: 20,
    maxZoom: 10
  }
  switch (getMapViewType()) {
    case INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS: {
      features = [...state.featuresByType.coordinates, ...state.featuresByType.shapes]
      break
    }
    case INVENTORY_MAP_VIEW_TYPES.IMPRESSIONS: {
      features = state.featuresByType.impressions
      options.pitch = INVENTORY_PITCH
      break
    }
    case INVENTORY_MAP_VIEW_TYPES.SCREENS: {
      features = state.featuresByType.screens
      break
    }
  }
  if (features.length === 0) {
    return
  }
  try {
    const bbox = getFeaturesExtent(features)
    map.fitBounds(bbox, options)
  } catch (error) {
    console.error(error)
    // nothing to do
  }
}

function createInventoryMapSources(map: Map): void {
  if (!map.getSource(SCREENS_SOURCE_ID)) {
    map.addSource(SCREENS_SOURCE_ID, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: state.featuresByType.screens
      },
      maxzoom: 24
    } as GeoJSONSourceRaw)
  }
  if (!map.getSource(IMPRESSIONS_SOURCE_ID)) {
    map.addSource(IMPRESSIONS_SOURCE_ID, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: state.featuresByType.impressions
      },
      maxzoom: 24
    } as GeoJSONSourceRaw)
  }
}

function refreshInventoryMapSources(): void {
  if (!state.inventory.map) {
    return
  }
  if (state.inventory.map.getSource(SCREENS_SOURCE_ID)) {
    setSourceData(state.inventory.map, SCREENS_SOURCE_ID, state.featuresByType.screens)
  }
  if (state.inventory.map.getSource(IMPRESSIONS_SOURCE_ID)) {
    setSourceData(state.inventory.map, IMPRESSIONS_SOURCE_ID, state.featuresByType.impressions)
  }
}

function applyDefaultViewType(): void {
  deactivateMapView(INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS)
  deactivateMapView(INVENTORY_MAP_VIEW_TYPES.SCREENS)
  deactivateMapView(INVENTORY_MAP_VIEW_TYPES.IMPRESSIONS)
  activateMapView(getMapViewType())
}

function applyViewTypeOnInventoryMap(toActivateViewType: INVENTORY_MAP_VIEW_TYPES): void {
  const currentViewType = getMapViewType()
  if (toActivateViewType === currentViewType) {
    return
  }
  deactivateMapView(currentViewType)
  activateMapView(toActivateViewType)
}

function activateMapView(toActivateViewType: INVENTORY_MAP_VIEW_TYPES): void {
  const currentViewType = getMapViewType()
  switch (toActivateViewType) {
    case INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS: {
      activateInventoryGeoTargetsView()
      break
    }
    case INVENTORY_MAP_VIEW_TYPES.SCREENS: {
      activateInventoryScreensView(currentViewType !== INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS)
      break
    }
    case INVENTORY_MAP_VIEW_TYPES.IMPRESSIONS: {
      activateInventoryImpressionsView(currentViewType !== INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS)
      break
    }
  }
}

function deactivateMapView(viewType: INVENTORY_MAP_VIEW_TYPES): void {
  switch (viewType) {
    case INVENTORY_MAP_VIEW_TYPES.GEO_TARGETS: {
      return deactivateInventoryGeoTargetsView()
    }
    case INVENTORY_MAP_VIEW_TYPES.SCREENS: {
      return deactivateInventoryScreensView()
    }
    case INVENTORY_MAP_VIEW_TYPES.IMPRESSIONS: {
      return deactivateInventoryImpressionsView()
    }
  }
}
